import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";

const EyeIcon = ({ off }) => off ? (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

function PwInput({ value, onChange, required, show, onToggle }) {
  return (
    <div style={{ position: "relative" }}>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        required={required}
        style={{ paddingRight: "2.5rem" }}
      />
      <button
        type="button"
        onClick={onToggle}
        style={{
          position: "absolute", right: "0.6rem", top: "50%",
          transform: "translateY(-50%)", background: "none", border: "none",
          cursor: "pointer", color: "#777", padding: 0, lineHeight: 0,
        }}
      >
        <EyeIcon off={show} />
      </button>
    </div>
  );
}

export default function AuthModal({ onClose }) {
  const { login } = useAuth();
  const [mode, setMode]       = useState("login"); // "login" | "register" | "forgot"
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm]       = useState({ email: "", passwort: "" });
  const [showLoginPw, setShowLoginPw]   = useState(false);

  const [registerForm, setRegisterForm] = useState({
    vorname: "", nachname: "", email: "", telefon: "",
    passwort: "", passwort2: "", werbungErwuenscht: false,
  });
  const [showRegPw, setShowRegPw]   = useState(false);
  const [showRegPw2, setShowRegPw2] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg]     = useState(null);

  const switchMode = (m) => { setMode(m); setError(null); setForgotMsg(null); };

  // ── Login ──────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const user = await api.auth.login(loginForm);
      login(user); onClose();
    } catch {
      setError("E-Mail oder Passwort falsch.");
    } finally { setLoading(false); }
  };

  // ── Register ───────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault(); setError(null);
    if (registerForm.passwort !== registerForm.passwort2) {
      setError("Passwörter stimmen nicht überein."); return;
    }
    setLoading(true);
    try {
      const user = await api.auth.register(registerForm);
      login(user); onClose();
    } catch {
      setError("Registrierung fehlgeschlagen. E-Mail möglicherweise schon vergeben.");
    } finally { setLoading(false); }
  };

  // ── Passwort vergessen ─────────────────────────────────
  const handleForgot = async (e) => {
    e.preventDefault(); setLoading(true); setForgotMsg(null);
    try {
      await api.auth.forgotPassword({ email: forgotEmail });
      setForgotMsg("✓ Falls diese E-Mail bei uns registriert ist, erhältst du in Kürze einen Reset-Link.");
    } catch {
      setForgotMsg("Fehler beim Senden. Bitte versuche es erneut.");
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1rem",
    }}>
      <div style={{
        background: "white", borderRadius: "16px", padding: "2rem",
        width: "100%", maxWidth: "420px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        maxHeight: "calc(100vh - 2rem)", overflowY: "auto",
      }}>

        {/* ── Tabs (nur bei login/register) ── */}
        {mode !== "forgot" && (
          <div style={{ display: "flex", marginBottom: "1.5rem", borderBottom: "2px solid #e0e0e0" }}>
            {["login", "register"].map((m) => (
              <button key={m} onClick={() => switchMode(m)} style={{
                flex: 1, padding: "0.75rem", border: "none", background: "none", cursor: "pointer",
                fontWeight: 700, fontSize: "1rem",
                color: mode === m ? "#2e7d32" : "#999",
                borderBottom: mode === m ? "3px solid #2e7d32" : "3px solid transparent",
                marginBottom: "-2px",
              }}>
                {m === "login" ? "Anmelden" : "Registrieren"}
              </button>
            ))}
          </div>
        )}

        {/* ── Header Passwort vergessen ── */}
        {mode === "forgot" && (
          <div style={{ marginBottom: "1.5rem" }}>
            <button onClick={() => switchMode("login")} style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#2e7d32", fontWeight: 600, padding: 0, fontSize: "0.9rem",
            }}>
              ← Zurück zur Anmeldung
            </button>
            <h3 style={{ marginTop: "0.75rem", color: "#2e7d32" }}>Passwort vergessen</h3>
          </div>
        )}

        {error && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{error}</div>}

        {/* ── Login Form ── */}
        {mode === "login" && (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>E-Mail</label>
              <input type="email" value={loginForm.email} required
                onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Passwort</label>
              <PwInput
                value={loginForm.passwort} required
                show={showLoginPw} onToggle={() => setShowLoginPw(s => !s)}
                onChange={e => setLoginForm({ ...loginForm, passwort: e.target.value })}
              />
            </div>
            <div style={{ textAlign: "right", marginTop: "-0.5rem", marginBottom: "1rem" }}>
              <button type="button" onClick={() => switchMode("forgot")} style={{
                background: "none", border: "none", color: "#2e7d32", cursor: "pointer",
                fontSize: "0.85rem", textDecoration: "underline", padding: 0,
              }}>
                Passwort vergessen?
              </button>
            </div>
            <button className="btn btn-success" type="submit" disabled={loading}
              style={{ width: "100%", marginTop: "0.5rem" }}>
              {loading ? "..." : "Anmelden"}
            </button>
          </form>
        )}

        {/* ── Register Form ── */}
        {mode === "register" && (
          <form onSubmit={handleRegister}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1rem" }}>
              <div className="form-group">
                <label>Vorname *</label>
                <input value={registerForm.vorname} required
                  onChange={e => setRegisterForm({ ...registerForm, vorname: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Nachname *</label>
                <input value={registerForm.nachname} required
                  onChange={e => setRegisterForm({ ...registerForm, nachname: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>E-Mail *</label>
              <input type="email" value={registerForm.email} required
                onChange={e => setRegisterForm({ ...registerForm, email: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Telefon</label>
              <input value={registerForm.telefon}
                onChange={e => setRegisterForm({ ...registerForm, telefon: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Passwort *</label>
              <PwInput
                value={registerForm.passwort} required
                show={showRegPw} onToggle={() => setShowRegPw(s => !s)}
                onChange={e => setRegisterForm({ ...registerForm, passwort: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Passwort wiederholen *</label>
              <PwInput
                value={registerForm.passwort2} required
                show={showRegPw2} onToggle={() => setShowRegPw2(s => !s)}
                onChange={e => setRegisterForm({ ...registerForm, passwort2: e.target.value })}
              />
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", margin: "0.5rem 0 1rem" }}>
              <input
                type="checkbox" id="werbung"
                checked={registerForm.werbungErwuenscht}
                onChange={e => setRegisterForm({ ...registerForm, werbungErwuenscht: e.target.checked })}
                style={{ width: "auto", flexShrink: 0, marginTop: "0.2rem" }}
              />
              <label htmlFor="werbung" style={{ marginBottom: 0, fontWeight: "normal", fontSize: "0.9rem" }}>
                Ich möchte Angebote und Neuigkeiten per E-Mail erhalten
              </label>
            </div>
            <button className="btn btn-success" type="submit" disabled={loading}
              style={{ width: "100%", marginTop: "0.5rem" }}>
              {loading ? "..." : "Konto erstellen"}
            </button>
            <p style={{ fontSize: "0.78rem", color: "#888", marginTop: "0.75rem", lineHeight: 1.5 }}>
              Mit der Registrierung stimmst du der Verarbeitung deiner personenbezogenen Daten gemäß
              unserer{" "}
              <a href="/datenschutz" style={{ color: "#2e7d32" }} onClick={onClose}>Datenschutzerklärung</a> zu.
            </p>
          </form>
        )}

        {/* ── Passwort vergessen Form ── */}
        {mode === "forgot" && (
          <form onSubmit={handleForgot}>
            {forgotMsg ? (
              <div className="alert alert-success">{forgotMsg}</div>
            ) : (
              <>
                <p style={{ color: "#555", marginBottom: "1rem", fontSize: "0.95rem" }}>
                  Gib deine E-Mail-Adresse ein – wir schicken dir einen Link zum Zurücksetzen.
                </p>
                <div className="form-group">
                  <label>E-Mail</label>
                  <input type="email" value={forgotEmail} required
                    onChange={e => setForgotEmail(e.target.value)} />
                </div>
                <button className="btn btn-success" type="submit" disabled={loading}
                  style={{ width: "100%", marginTop: "0.5rem" }}>
                  {loading ? "..." : "Reset-Link senden"}
                </button>
              </>
            )}
          </form>
        )}

        <button onClick={onClose} style={{
          marginTop: "1rem", width: "100%", padding: "0.5rem",
          border: "none", background: "none", color: "#999", cursor: "pointer",
        }}>
          Schließen
        </button>
      </div>
    </div>
  );
}
